import type { MentionKind } from "@repo/shared/types/mention-part";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { blocks$ } from "~/stores/blocks/blocks.crud";

type Navigate = ReturnType<typeof useNavigate>;

/**
 * Side-effect a mention chip dispatches when clicked. Receives the entity id
 * and a router `navigate` function so each handler can pick the right route /
 * post-navigation state mutation for its kind.
 */
type MentionAction = (ctx: {
  id: string;
  navigate: Navigate;
}) => Promise<void> | void;

/**
 * Per-kind navigation registry for mention chips. Mirrors the server-side
 * `mentionResolvers` shape in [apps/api/src/mastra/mentions/registry.ts] —
 * adding a new mentionable kind is a one-entry change here.
 */
export const mentionActions: Record<MentionKind, MentionAction> = {
  dataset: ({ id, navigate }) =>
    navigate({ to: "/datasets/$datasetId", params: { datasetId: id } }),
  datasource: ({ id, navigate }) =>
    navigate({
      to: "/settings/datasources/$datasourceId",
      params: { datasourceId: id },
    }),
  board: ({ id, navigate }) =>
    navigate({ to: "/boards/$boardId", params: { boardId: id } }),
  block: ({ id, navigate }) => {
    const boardId = blocks$[id].boardId.get();
    if (!boardId) return;
    // The board route's `beforeLoad` seeds `boardPage$.selectedBlockId` from
    // `search.blockId`, and `BoardPage` scrolls/focuses the matching grid
    // item — so we just route there with the right URL shape.
    return navigate({
      to: "/boards/$boardId",
      params: { boardId },
      search: { blockId: id },
    });
  },
};

/**
 * Returns a stable dispatcher that runs the registered action for a given
 * mention kind/id pair. Use from any component rendering a clickable chip.
 */
export const useMentionAction = () => {
  const navigate = useNavigate();
  return useCallback(
    (kind: MentionKind, id: string) => {
      void mentionActions[kind]({ id, navigate });
    },
    [navigate],
  );
};
